import { useEffect, useMemo, useRef, useState } from 'react';
import type { FlatEntry } from '../navigation/modules';

/**
 * Paleta de comandos (Ctrl/Cmd+K) — busca em todas as telas do sistema.
 *
 * Porta o comportamento do protótipo (`base-html/app/assets/app.js`), incluindo a
 * normalização NFD: "conciliacao" precisa achar "Conciliação Bancária", porque
 * ninguém digita acento numa busca rápida.
 */

interface CommandPaletteProps {
  readonly open: boolean;
  readonly entries: readonly FlatEntry[];
  readonly onClose: () => void;
  readonly onSelect: (path: string) => void;
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

const MAX_HITS = 60;

export function CommandPalette({
  open,
  entries,
  onClose,
  onSelect,
}: CommandPaletteProps): JSX.Element | null {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const hits = useMemo(() => {
    const nq = normalize(query).trim();
    const tokens = nq ? nq.split(/\s+/) : [];
    const matched = entries.filter((e) => {
      if (tokens.length === 0) return true;
      const hay = normalize(`${e.label} ${e.moduleLabel} ${e.path}`);
      return tokens.every((t) => hay.includes(t));
    });
    return matched.slice(0, MAX_HITS);
  }, [entries, query]);

  // reabrir a paleta sempre começa limpa, como no protótipo
  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    setCursor((c) => (hits.length === 0 ? 0 : Math.min(c, hits.length - 1)));
  }, [hits.length]);

  useEffect(() => {
    listRef.current
      ?.querySelector('[data-cursor="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [cursor, hits.length]);

  if (!open) return null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (hits.length === 0 ? 0 : (c + 1) % hits.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (hits.length === 0 ? 0 : (c - 1 + hits.length) % hits.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[cursor];
      if (hit) {
        onSelect(hit.path);
        onClose();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex justify-center pt-[12vh]">
      <button
        type="button"
        aria-label="Fechar busca"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[rgba(0,16,36,0.45)] backdrop-blur-[2px]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Buscar telas"
        className="relative flex max-h-[70vh] w-[min(640px,92vw)] flex-col overflow-hidden rounded-xl border border-outline-variant bg-surface-container-lowest shadow-[0_16px_48px_rgba(0,16,36,0.28)]"
      >
        <div className="flex items-center gap-sm border-b border-outline-variant px-md py-sm">
          <span className="material-symbols-outlined text-on-surface-variant">search</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Buscar tela ou módulo…"
            autoComplete="off"
            spellCheck={false}
            className="flex-1 border-0 bg-transparent font-body-lg text-body-lg text-on-surface outline-none placeholder:text-on-surface-variant focus:ring-0"
          />
          <kbd className="rounded border border-b-2 border-outline-variant px-xs py-[2px] font-label-sm text-label-sm text-on-surface-variant">
            Esc
          </kbd>
        </div>

        <div ref={listRef} className="overflow-y-auto p-xs">
          {hits.length === 0 ? (
            <p className="p-lg text-center font-body-md text-body-md text-on-surface-variant">
              Nenhuma tela encontrada.
            </p>
          ) : (
            hits.map((hit, i) => (
              <button
                key={hit.path}
                type="button"
                data-cursor={i === cursor}
                onMouseEnter={() => setCursor(i)}
                onClick={() => {
                  onSelect(hit.path);
                  onClose();
                }}
                className={`flex w-full items-center gap-sm rounded-lg px-sm py-xs text-left ${
                  i === cursor ? 'bg-surface-container' : 'bg-transparent'
                }`}
              >
                <span className="material-symbols-outlined text-[18px] text-on-surface-variant">
                  {hit.moduleIcon}
                </span>
                <span className="min-w-0 flex-1 truncate font-body-md text-body-md text-on-surface">
                  {hit.label}
                </span>
                <span className="shrink-0 font-label-sm text-label-sm text-on-surface-variant">
                  {hit.moduleLabel}
                </span>
              </button>
            ))
          )}
        </div>

        <div className="flex gap-md border-t border-outline-variant px-md py-xs font-label-sm text-label-sm text-on-surface-variant">
          <span>↑↓ navegar</span>
          <span>Enter abrir</span>
          <span>Esc fechar</span>
        </div>
      </div>
    </div>
  );
}
